/* eslint-disable import/no-extraneous-dependencies */
import { Grid } from '@mui/material';
import { BasicButton } from '@tassat/button';
import {
    SelectField,
    SimpleErrorMessage,
    TextField
} from '@tassat/formik-input';
import {
    ActionTypes,
    AdminRoleId,
    AdminRolesMap
} from '@tassat/ui-definitions';
import { debounceExecution } from '@tassat/utility';
import { Formik, FormikProps } from 'formik';
import * as React from 'react';

import { validateAdminUserForm } from './submissions';
import { IFormProps, IFormValues, IFormikProps } from './types';
import { emptySelectOption } from '../helpers/constant';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        values,
        errors,
        submitForm,
        isValid,
        isSubmitting,
        dirty,
        formProps,
        submitError,
        setValues,
        isUsernameValidated,
        setIsUsernameValidated,
        validateForm
    } = props;

    const {
        actionType,
        editUser,
        deleteAdminUser,
        userRoles,
        pcgSelectOptions,
        features
    } = formProps;

    const mappedUserRolesMemo = React.useMemo(() => userRoles.reduce((acc, role) => {
        const label = role.DisplayName || AdminRolesMap[role.UserRoleType] || '';
        acc.push({
            label,
            value: role.AdminRoleId
        });

        return acc;
    }, [] as { label: string; value: string }[]), [userRoles]);

    React.useEffect(() => {
        if (features?.loginType === 'Auth0' && !features?.SSO) {
            setValues({
                ...values,
                Username: values.Email
            });
        }
    }, [ features?.loginType, features?.SSO, values.Email ]);

    React.useEffect(() => {
        if (!(actionType === ActionTypes.edit || (actionType === ActionTypes.add && features?.loginType === 'Auth0' && !features.SSO))) {
            setIsUsernameValidated(false);
        }
    }, [values.Username]);

    React.useEffect(() => {
        if (!isUsernameValidated) {
            validateForm();
        }
    }, [isUsernameValidated]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    sm={12}
                >
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            disabled={features?.SSO && (actionType === ActionTypes.sso_validate || actionType === ActionTypes.edit)}
                            errors={errors}
                            label='First Name'
                            maxLength={48}
                            name='FirstName'
                            placeholder='First'
                            testId='input-first-name'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            disabled={features?.SSO && (actionType === ActionTypes.sso_validate || actionType === ActionTypes.edit)}
                            errors={errors}
                            label='Last Name'
                            maxLength={48}
                            name='LastName'
                            placeholder='Last'
                            testId='input-last-name'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    sm={12}
                >
                    <Grid
                        item
                        md={5}
                        sm={12}
                        test-id='admin-user-form--username'
                    >
                        <TextField
                            disabled={actionType === ActionTypes.edit ||
                                (actionType === ActionTypes.sso_validate && features?.SSO) ||
                                (actionType === ActionTypes.add && features?.loginType === 'Auth0' && !features?.SSO)}
                            errors={errors}
                            label={features?.SSO ? 'SSO User ID' : 'Username'}
                            maxLength={features?.SSO ? 256 : 64}
                            name='Username'
                            placeholder={features?.SSO ? 'SSO User ID' : 'Username'}
                            testId='input-username'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            disabled={
                                (actionType === ActionTypes.edit) ||
                                (actionType === ActionTypes.sso_validate && features?.SSO)
                            }
                            errors={errors}
                            label='Email'
                            maxLength={64}
                            name='Email'
                            placeholder='Email'
                            testId='input-email'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    sm={12}
                >
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <SelectField
                            autoComplete
                            errors={errors}
                            label='Role'
                            name='AdminRoleId'
                            options={mappedUserRolesMemo}
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            disabled={features?.SSO && (actionType === ActionTypes.sso_validate || actionType === ActionTypes.edit)}
                            errors={errors}
                            label='Phone'
                            maxLength={32}
                            name='Phone'
                            placeholder='Phone Number'
                            testId='input-phone-number'
                            values={values}

                        />
                    </Grid>
                    {
                        values.AdminRoleId &&
                        values.AdminRoleId.value === AdminRoleId.PCG_ADMIN && (
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <SelectField
                                        autoComplete
                                        errors={errors}
                                        hasAllOption={!(features?.SSO && actionType === ActionTypes.sso_validate)}
                                        label='PCG Mapping'
                                        //Added this change for sso and the validate user call only to only allow a single pcg number.
                                        multiSelect={!(features?.SSO && actionType === ActionTypes.sso_validate)}
                                        name='PCGMapping'
                                        options={pcgSelectOptions}
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                />
                            </Grid>
                        )
                    }
                </Grid>
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    sm={12}
                >
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            errors={errors}
                            label='Notes'
                            maxLength={50}
                            name='Notes'
                            placeholder='Notes'
                            testId='input-notes'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        md={5}
                        sm={12}
                    />
                </Grid>
                <Grid
                    alignItems='center'
                    className={classes.submitButtonContainer}
                    container
                    item
                    justifyContent='space-evenly'
                    xs={12}
                >
                    {(actionType === ActionTypes.edit ||
                        (features?.SSO && actionType === ActionTypes.sso_validate)) && (
                        <Grid item>
                            <BasicButton
                                onClick={() => deleteAdminUser && deleteAdminUser(editUser)}
                                testId='button-delete-admin-user'
                                text='Delete Admin User'
                                type='action'
                            />
                        </Grid>
                    )}
                    <Grid
                        alignItems='center'
                        className={classes.submitButton}
                        container
                        direction='column'
                        item
                    >
                        <BasicButton
                            disabled={!dirty ||
                                isSubmitting ||
                                !isValid || !isUsernameValidated}
                            onClick={submitForm}
                            testId='button-submit'
                            text={features?.SSO ? 'Save Changes' : 'Submit'}
                            type='submit'
                        />
                        <SimpleErrorMessage
                            className={classes.submitError}
                            error={submitError}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export function AdminUserForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        actionType,
        editUser,
        users,
        features,
        submitAdminUserForm,
        validateUsernameAsync
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');
    const [ isUsernameValidated, setIsUsernameValidated ] = React.useState<boolean>((actionType === ActionTypes.edit || (actionType === ActionTypes.add && features?.loginType === 'Auth0' && !features.SSO)) ? true : false);
    const debouncedUsernameValidation = React.useRef(debounceExecution<string | null>(validateUsernameAsync, 1000));

    let initialValues = {} as IFormValues;

    if (actionType === ActionTypes.sso_validate && features?.SSO) {
        initialValues = {
            FirstName: editUser?.FirstName ?? '',
            LastName: editUser?.LastName ?? '',
            Email: editUser?.Email ?? '',
            Phone: editUser?.Phone ?? '',
            Username: editUser?.Username,
            Notes: '',
            AdminRoleId: emptySelectOption,
            PCGMapping: []
        };
    } else if (actionType === ActionTypes.sso_add && features?.SSO) {
        initialValues = {
            FirstName: '',
            LastName: '',
            Email: '',
            Phone: '',
            Notes: '',
            AdminRoleId: emptySelectOption,
            PCGMapping: [],
            Username: ''
        };
    } else if (actionType === ActionTypes.add) {
        initialValues = {
            FirstName: '',
            LastName: '',
            Email: '',
            Phone: '',
            Notes: '',
            AdminRoleId: emptySelectOption,
            PCGMapping: [],
            Username: ''
        };
    } else if (actionType === ActionTypes.edit) {
        if (!editUser) return <div />;

        let PCGMapping: { label: string; value: string }[] | undefined;
        if (editUser.AdminRoleId === AdminRoleId.PCG_ADMIN) {
            PCGMapping = editUser.PcgMapping?.map((pcg) => ({
                label: pcg.PCGNumber,
                value: pcg.PCGId
            }));
        }

        const UserRoleId = {
            label: editUser.UserRoleDisplay || '',
            value: editUser.AdminRoleId
        };

        initialValues = {
            ...editUser,
            PCGMapping,
            AdminRoleId: UserRoleId
        };
    }

    const adminUserOriginalValidations = (values: IFormValues) => validateAdminUserForm(values, {
        actionType,
        initialValues,
        isAdmin: true,
        firmId: '-1',
        users,
        features,
        debouncedUsernameValidation: debouncedUsernameValidation.current,
        isUsernameValidated,
        setIsUsernameValidated
    });

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                sm={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        try {
                            await submitAdminUserForm(values, {
                                actionType,
                                initialValues,
                                isAdmin: true,
                                firmId: '-1',
                                editUser,
                                features,
                                onClose,
                                setSubmitError
                            });
                        } catch (error) {
                            setSubmitError('Something went wrong');
                        }
                    }}
                    validate={adminUserOriginalValidations}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentProps}
                            classes={classes}
                            features={features}
                            formProps={formProps}
                            isUsernameValidated={isUsernameValidated}
                            setIsUsernameValidated={setIsUsernameValidated}
                            submitError={submitError}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}